// src/hooks/useArticle.ts

import { useEffect, useState, useCallback } from 'react'
import { fetchArticleById, fetchArticles, fetchAllArticles } from '../services/articleService'
import type { Article } from '../types/article'

interface UseArticleState {
  article: Article | null
  loading: boolean
  error: string | null
}

interface UseArticleReturn extends UseArticleState {
  refetch: () => void
}

/**
 * Hook custom pour récupérer un article par son UUID.
 * Utilisé dans ArticleDetailPage.
 *
 * Le slug est ignoré ici — seul l'id est envoyé à l'API.
 *
 * @example
 * const { article, loading, error, refetch } = useArticle('3fa85f64-5717-4562-b3fc-2c963f66afa6')
 */
export function useArticle(id: string): UseArticleReturn {
  const [state, setState] = useState<UseArticleState>({
    article: null,
    loading: true,
    error: null,
  })

  const load = useCallback(async () => {
    if (!id) {
      console.warn('[useArticle] id manquant — chargement annulé')
      return
    }

    console.debug('[useArticle] chargement démarré', { id })
    setState({ article: null, loading: true, error: null })
    try {
      const article = await fetchArticleById(id)
      console.debug('[useArticle] chargement réussi', {
        id: article.id,
        title: article.title,
      })
      setState({ article, loading: false, error: null })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue.'
      console.error('[useArticle] chargement échoué', { id, error: message })
      setState({ article: null, loading: false, error: message })
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  return { ...state, refetch: load }
}

/**
 * Hook custom pour récupérer un article par son slug.
 *
 * Stratégie :
 *  1. Charge la liste des articles publiés (GET /api/article).
 *  2. Cherche l'article dont le slug correspond.
 *  3. Si non trouvé et qu'un token admin est fourni, recharge via
 *     GET /api/article/admin pour inclure les articles non publiés.
 *  4. Une fois l'article localisé (id connu), charge le détail complet
 *     via GET /api/article/{id}.
 *
 * L'URL reste /blog/:slug — l'id ne transit jamais dans l'URL.
 *
 * @example
 * const { article, loading, error, refetch } = useArticleBySlug('mon-article', token)
 */
export function useArticleBySlug(slug: string, token?: string | null): UseArticleReturn {
  const [state, setState] = useState<UseArticleState>({
    article: null,
    loading: true,
    error: null,
  })

  const load = useCallback(async () => {
    if (!slug) {
      console.warn('[useArticleBySlug] slug manquant — chargement annulé')
      return
    }

    console.debug('[useArticleBySlug] chargement démarré', { slug })
    setState({ article: null, loading: true, error: null })
    try {
      // 1. Cherche dans les articles publics
      let articles = await fetchArticles()
      let found = articles.find((a) => a.slug === slug)

      // 2. Si non trouvé et token admin dispo, cherche aussi les non publiés
      if (!found && token) {
        const adminArticles = await fetchAllArticles(token)
        found = adminArticles.find((a) => a.slug === slug)
      }

      if (!found) {
        throw new Error('Article introuvable.')
      }

      // 3. Charge le détail complet par id (contient le content Markdown)
      const article = await fetchArticleById(found.id)
      console.debug('[useArticleBySlug] chargement réussi', {
        id: article.id,
        title: article.title,
        slug: article.slug,
      })
      setState({ article, loading: false, error: null })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue.'
      console.error('[useArticleBySlug] chargement échoué', { slug, error: message })
      setState({ article: null, loading: false, error: message })
    }
  }, [slug, token])

  useEffect(() => {
    load()
  }, [load])

  return { ...state, refetch: load }
}
